/**
 * Copyright (c) 2018-present, Didi, Inc.
 * All rights reserved.
 *
 * @author Cory(kuanghongrui@didichuxing.com)
 *
 * @file The class of PageViewEvent
 */

import { ITrackerContext } from 'com/didichuxing/tracker/context/ITrackerContext';
import { IAttrs } from 'com/didichuxing/tracker/IAttrs';
import SequencedEvent from 'com/didichuxing/tracker/plugin/events/SequencedEvent';
import PluginUtils from 'com/didichuxing/tracker/plugin/utils/PluginUtils';
import { IPerformanceMemory, IPerformanceTiming } from 'com/didichuxing/tracker/h5/plugins/IPerformance';
import { IPlanObject } from 'com/didichuxing/IPlanObject';
import { IObjectMap } from 'com/didichuxing/IObjectMap';

interface IPVExtraAttrs extends IAttrs {

    /**
     * @readonly
     * 载入当前文档的文档的 URL
     */
    readonly referrer: string;

    /**
     * @readonly
     * cookie是否开启
     */
    readonly cookieEnabled: boolean;

    /**
     * @readonly
     * 当前文档标题
     */
    readonly pageTitle: string;

    /**
     * @readonly
     * 当前访问协议
     */
    readonly protocol: string;

    /**
     * @readonly
     * 当前访问主机地址。
     */
    readonly host: string;

    /**
     * @readonly
     * 当前访问路径
     */
    readonly path: string;

    /**
     * @readonly
     * 当前访问hash
     */
    readonly hash: string;

    /**
     * @readonly
     * 当前访问search值。
     */
    readonly search: string;

    /**
     * @readonly
     * 当前设备屏幕高。
     */
    readonly screenHeight: number;

    /**
     * @readonly
     * 当前设备屏幕宽。
     */
    readonly screenWidth: number;

    /**
     * @readonly
     * 当前设备颜色深度
     */
    readonly colorDepth: string;

    /**
     * @readonly
     * 当前设备供应商。
     */
    readonly deviceVendor: string;
}

export default class OMGH5PageViewEvent extends SequencedEvent {

    /**
     * 是否首次曝光
     * - 1 首次
     * - 0 非首次
     */
    private isFirst: string = '1';

    constructor(attrs: IAttrs, context: any, info?: IAttrs) {
        super(attrs, context, info);
        this.initFirtst();
    }
    private initFirtst() {
        try {
            this.isFirst = (this.getContext() as any).isFirstSw ? '1' : '0';
            (this.getContext() as any).isFirstSw = false;
        } catch (e) {
            console.info('【OMEGA】曝光设置初始化状态异常', e);
        }
    }

    /**
     * @override
     * @inheritDoc
     * @returns {string}
     */
    public getType(): string {
        return PluginUtils.EVENT_ID_PAGE_VIEW;
    }

    /**
     * 获取全局性能信息。
     * {@link https://github.com/addyosmani/timing.js/issues/9}
     * @returns {Performance}
     */
    private get globalPerformance(): Performance {
        const global: any = window;
        return global.performance || global.webkitPerformance || global.msPerformance || global.mozPerformance;
    }

    /**
     * 获取时间方面性能数据。
     * {@link https://www.w3.org/TR/navigation-timing/}
     * @returns {IPerformance}
     */
    protected get performanceTiming(): IPerformanceTiming {
        const timing: PerformanceTiming = this.globalPerformance.timing;
        let FP: any = {};
        let FCP: any = {};
        let FMP: any = {};
        try {
            if (performance.getEntriesByName) {
                FP = performance.getEntriesByName('first-paint')[0];
                FCP = performance.getEntriesByName('first-contentful-paint')[0];
                FMP = performance.getEntriesByName('fmp')[0];
            }
        } catch (error) {
            console.info('未采集到性能数据');
        }
        return {
            redirectDuration: timing.fetchStart - timing.navigationStart,
            dnsDuration: timing.domainLookupEnd - timing.domainLookupStart,
            tcpDuration: timing.connectEnd - timing.connectStart,
            ttfbDuration: timing.responseStart - timing.requestStart,
            transDuration: timing.responseEnd - timing.responseStart,
            domParsedDuration: timing.domInteractive - timing.responseEnd,
            resourceLoadDuration: timing.loadEventStart - timing.domContentLoadedEventEnd,
            firstByteTime: timing.responseStart - timing.domainLookupStart,
            htmlLoadCompleteTime: timing.responseEnd - timing.navigationStart,
            firstPaintTime: FP && FP.startTime,
            firstContentfulPaintTime: FCP && FCP.startTime,
            firstMeaningfulPaintTime: FMP && FMP.startTime,
            interactTime: timing.domInteractive - timing.navigationStart,
            domReadyTime: timing.domContentLoadedEventEnd - timing.navigationStart,
            pageLoadTime: timing.loadEventStart - timing.navigationStart,
            domContentLoadedTime: timing.domContentLoadedEventEnd - timing.navigationStart,
            domCompleteTime: timing.domComplete - timing.navigationStart,
            trackerSDKLoadDuration: this.trackerSDKLoadDuration,
            loadEventTime: timing.loadEventEnd - timing.navigationStart
        };
    }

    /**
     * 获取内存方面性能数据。
     * @returns {IPerformanceMemory}
     */
    protected get performanceMemory(): IPerformanceMemory {
        const memory: IPlanObject = (this.globalPerformance as any).memory || {};
        return {
            usedJSHeapSize: memory.usedJSHeapSize as number || 0,
            totalJSHeapSize: memory.totalJSHeapSize as number || 0,
            jsHeapSizeLimit: memory.jsHeapSizeLimit as number || 0
        };
    }

    /**
     * tracker sdk包加载的时间。
     * @returns {number}
     */
    protected get trackerSDKLoadDuration(): number {
        if (this.globalPerformance && this.globalPerformance.getEntries) {
            const entries: PerformanceEntry[] = this.globalPerformance.getEntries();
            let trackerPerformanceEntry: PerformanceEntry | undefined;
            entries.some((entry: PerformanceEntry): boolean => {
                const initiatorType: string = (entry as PerformanceResourceTiming).initiatorType;
                if (initiatorType && initiatorType === 'script' && /\/omega[\.\w]*\.min\.js$/.test(entry.name)) {
                    trackerPerformanceEntry = entry;
                    return true;
                }
                return false;
            });
            if (trackerPerformanceEntry) {
                return Math.round(trackerPerformanceEntry.duration);
            }
        }
        return 0;
    }

    /**
     * 获得pv上报时所带的性能数据。
     * @returns {(IAttrs & IPerformanceTiming & IPerformanceMemory) | IAttrs}
     */
    private get pvPerformanceAttrs(): (IAttrs & IPerformanceTiming & IPerformanceMemory) | IAttrs {
        if (this.globalPerformance) {
            return {
                ...this.performanceTiming,
                ...this.performanceMemory
            };
        }
        return {};
    }

    /**
     * 获取webView的加载时间
     * - 由端上开发注入
     */
    private get webViewLoadTime(): undefined | number {
        const global: any = window;
        // 如果在webView里面存在加载时间
        if (global.FKMetrics && global.FKMetrics.webviewLoadTime) {
            return global.FKMetrics.webviewLoadTime;
        }
        return undefined;
    }
    private getOffMode(): string | undefined {
        let offMode: string|undefined;
        try {
            /**
             * app识别离线模式
             * 桥接方式识别
             */
            if (window && (window as any).FKConfig && (window as any)['FKConfig'].getOffMode) {
                offMode = (window as any)['FKConfig'].getOffMode();
            } else if (navigator.userAgent && navigator.userAgent.indexOf('OffMode/') !== -1) {
                // 通过userAgent识别
                const val: string = navigator.userAgent.split('OffMode/')[1] || '';
                offMode = val[0];
            }
        } catch (e) {
            console.info('off mode error', e);
        }
        return offMode;
    }

    /**
     * 获取PV额外的属性数据。
     * @returns {IPVExtraAttrs}
     */
    private get pvExtraAttrs(): IPVExtraAttrs {
        const document: Document = window.document || {documentElement: {}};
        const navigator: Navigator = window.navigator || {};
        const location: Location = window.location || {};
        const screen: Screen = window.screen || {};
        const context: any = this.getContext();
        let search: string = location.search;
        if (context && context.noUrlParam) {
            search = '';
        }
        const offMode: string | undefined = this.getOffMode();
        return {
            off_mode: offMode,
            t_wlt: this.webViewLoadTime,
            referrer: document.referrer,
            cookieEnabled: navigator.cookieEnabled,
            pageTitle: (this.getContext() as any).pageTitle || document.title,
            protocol: location.protocol,
            host: location.host,
            path: location.pathname,
            hash: location.hash,
            search,
            screenWidth: document.documentElement.clientWidth,
            screenHeight: document.documentElement.clientHeight,
            colorDepth: screen.colorDepth ? `${ screen.colorDepth }-bit` : '',
            deviceVendor: (this.getContext() as ITrackerContext).deviceVendor || ''
        };
    }

    /**
     * @override
     * @inheritDoc
     * @returns {IAttrs}
     */
    protected getAttrs(): IAttrs {
        // 是否首次曝光
        const ft = this.isFirst;
        return {
            ft,
            ...this.pvExtraAttrs,
            ...this.pvPerformanceAttrs,
            ...super.getAttrs()
        };
    }

    /**
     * @override
     * @inheritDoc
     * @returns {IObjectMap<string>}
     */
    protected getAttrsKeyMap(): IObjectMap<string> {
        return {
            ...super.getAttrsKeyMap(),
            ...this.getPVExtraAttrsKeyMap(),
            ...this.getPerformanceTimingAttrsKeyMap(),
            ...this.getPerformanceMemoryAttrsKeyMap()
        };
    }

    /**
     * PV额外上报属性键值对映射实体。
     * @returns {IObjectMap<string>}
     */
    private getPVExtraAttrsKeyMap(): IObjectMap<string> {
        return {
            referrer: 'r',
            cookieEnabled: 'c',
            pageTitle: 'pt',
            protocol: 'upr',
            host: 'uho',
            path: 'upa',
            hash: 'uha',
            search: 'us',
            screenHeight: 'sh',
            screenWidth: 'sw',
            colorDepth: 'cd',
            deviceVendor: 'm',
            pvType: 'pv_t'
        };
    }

    /**
     * 耗时相关性能属性键值对映射实体。
     * @see {@interface com/didichuxing/tracker/h5/plugins/pv/IPerformance}
     * @returns {IObjectMap<string>}
     */
    private getPerformanceTimingAttrsKeyMap(): IObjectMap<string> {
        return {
            dnsDuration: 't_dns',
            tcpDuration: 't_tcp',
            ttfbDuration: 't_rq',
            transDuration: 't_trans',
            domParsedDuration: 't_psdm',
            resourceLoadDuration: 't_res',
            firstByteTime: 't_fb',
            htmlLoadCompleteTime: 't_hlc',
            firstPaintTime: 't_fp',
            firstContentfulPaintTime: 't_fcp',
            firstMeaningfulPaintTime: 't_fmp',
            interactTime: 't_di',
            domReadyTime: 't_dmrd',
            pageLoadTime: 't_pgrd',
            domContentLoadedTime: 't_dcl',
            domCompleteTime: 't_dc',
            trackerSDKLoadDuration: 't_omgld',
            loadEventTime: 't_ld'
        };
    }

    /**
     * 内存相关性能属性键值对映射实体。
     * @see {@interface com/didichuxing/tracker/h5/plugins/pv/IPerformance}
     * @returns {IObjectMap<string>}
     */
    private getPerformanceMemoryAttrsKeyMap(): IObjectMap<string> {
        return {
            usedJSHeapSize: 'm_used',
            totalJSHeapSize: 'm_total',
            jsHeapSizeLimit: 'm_limit'
        };
    }
}
